import React from 'react';
import { Card, Row, Col, Statistic } from 'antd';
import {
  DollarOutlined,
  TransactionOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';

interface TransactionStatisticsProps {
  transactions: any[];
  financialRecords?: any[];
  activeTab?: string;
  loading: boolean;
  formatCurrency: (amount: number) => string;
}

const TransactionStatistics: React.FC<TransactionStatisticsProps> = ({
  transactions,
  financialRecords = [],
  activeTab = 'combined',
  loading,
  formatCurrency,
}) => {
  // Calculate statistics based on active tab
  let depositAmount = 0;
  let withdrawalAmount = 0;
  let completedTransactions = 0;
  let pendingTransactions = 0;
  let totalIncome = 0;
  let totalExpense = 0;

  // Tổng "thu" và "chi" gắn với giao dịch đáo hạn trong ngày — dùng cho
  // 2 stat mới "Tổng giao dịch đáo hạn" và "Tổng đáo tổng hợp" (customer
  // 2026-05-23). financialRecords ở đây là các bản ghi có
  // relatedTransactionId IS NOT NULL (xem Maturity-transactions-by-month
  // loadFinancialRecords).
  totalIncome = financialRecords
    .filter((r) => r.recordType === 'INCOME')
    .reduce((sum, r) => sum + (r.amount || 0), 0);
  totalExpense = financialRecords
    .filter((r) => r.recordType === 'EXPENSE')
    .reduce((sum, r) => sum + (r.amount || 0), 0);

  if (activeTab === 'financial') {
    completedTransactions = financialRecords.length; // Tất cả thu chi đều đã hoàn thành
    pendingTransactions = 0; // Không có trạng thái chờ xử lý cho thu chi
  } else {
    // Tính thống kê cho tab giao dịch và tổng hợp
    depositAmount = transactions
      .filter((t) => t.transactionType === 'deposit')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
    withdrawalAmount = transactions
      .filter((t) => t.transactionType === 'withdrawal')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
    completedTransactions = transactions.filter(
      (t) => t.status === 'COMPLETED'
    ).length;
    pendingTransactions = transactions.filter(
      (t) => t.status === 'PENDING'
    ).length;
  }

  // Customer 2026-05-23 + 2026-05-27:
  //   - Tab "Tổng hợp" (combined): "Tổng giao dịch đáo hạn" = nạp + chi,
  //     "Tổng đáo tổng hợp" = nạp + chi + thu (toàn bộ).
  //   - Tab "Giao dịch" (transactions): "Tổng giao dịch đáo hạn" = chỉ nạp
  //     tiền, KHÔNG cộng chi (khách hàng 27/05: "khi chọn Giao dịch, nó
  //     tính sai vì cộng cả thu chi"). Không hiển thị "Tổng đáo tổng hợp"
  //     vì semantic của nó là combined view.
  const isCombinedTab = activeTab === 'combined';
  const totalGiaoDichDaoHan = isCombinedTab
    ? depositAmount + totalExpense
    : depositAmount;
  const totalDaoTongHop = depositAmount + totalExpense + totalIncome;

  return (
    <div className="mb-6">
      <Row gutter={[16, 16]}>
        {activeTab === 'financial' ? (
          // Thống kê cho tab thu chi
          <>
            <Col xs={24} sm={12} lg={6}>
              <Card className="h-full">
                <Statistic
                  title="Tổng chi (VNĐ)"
                  value={formatCurrency(totalExpense)}
                  prefix={<TransactionOutlined className="text-red-500" />}
                  loading={loading}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="h-full">
                <Statistic
                  title="Số bản ghi"
                  value={financialRecords.length}
                  prefix={<CheckCircleOutlined className="text-blue-500" />}
                  loading={loading}
                />
              </Card>
            </Col>
          </>
        ) : (
          // Thống kê cho tab giao dịch và tổng hợp
          <>
            <Col xs={24} sm={12} lg={8}>
              <Card className="h-full">
                <Statistic
                  title="Tổng giao dịch đáo hạn (VNĐ)"
                  value={formatCurrency(totalGiaoDichDaoHan)}
                  prefix={<DollarOutlined className="text-blue-500" />}
                  loading={loading}
                />
              </Card>
            </Col>

            {isCombinedTab && (
              <Col xs={24} sm={12} lg={8}>
                <Card className="h-full">
                  <Statistic
                    title="Tổng đáo tổng hợp (VNĐ)"
                    value={formatCurrency(totalDaoTongHop)}
                    prefix={<DollarOutlined className="text-indigo-500" />}
                    loading={loading}
                  />
                </Card>
              </Col>
            )}

            <Col xs={24} sm={12} lg={8}>
              <Card className="h-full">
                <Statistic
                  title="Tổng rút (VNĐ)"
                  value={formatCurrency(withdrawalAmount)}
                  prefix={<TransactionOutlined className="text-purple-500" />}
                  loading={loading}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={12}>
              <Card className="h-full">
                <Statistic
                  title="Đã hoàn thành"
                  value={completedTransactions}
                  prefix={<CheckCircleOutlined className="text-green-500" />}
                  loading={loading}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={12}>
              <Card className="h-full">
                <Statistic
                  title="Chờ xử lý"
                  value={pendingTransactions}
                  prefix={<ClockCircleOutlined className="text-orange-500" />}
                  loading={loading}
                />
              </Card>
            </Col>
          </>
        )}
      </Row>
    </div>
  );
};

export default TransactionStatistics;
